import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { UserBlockingError } from '@hookmyapp/observability/errors';
import { useWorkspace } from '@/contexts/workspace-context';
import { useError } from '@/contexts/error-context';

// Mirror use-fetch.ts so the SSE handshake hits the same backend origin as
// the rest of the authenticated REST calls. When VITE_API_URL is set the
// frontend and backend live on different origins; opening relative
// `/sandbox/sessions/stream` would 404 against the frontend host.
const API_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * SSE replacement for polling/focus-refetch on /sandbox/sessions.
 * Subscribes to GET /sandbox/sessions/stream using @microsoft/fetch-event-source
 * so Bearer + X-Workspace-Id headers are sent (native EventSource is
 * cookie-only and cannot set Authorization or X-Workspace-Id).
 *
 * Applies five event types to a local sessions list:
 *   - snapshot                          (replace)
 *   - sandbox.session.bound             (upsert)
 *   - sandbox.session.activated         (upsert)
 *   - sandbox.session.deactivated       (status update)
 *   - sandbox.session.updated           (upsert)
 *   - sandbox.session.deleted           (remove)
 *
 * Dedup via per-mount Set<string> of seen eventIds — Pub/Sub redelivery
 * (or test-seam double-emit) cannot double-apply state changes.
 *
 * Test seam: window.__mockSandboxStreamEmit(event, payload) synthesizes
 * server frames so Playwright doesn't need a running backend. Mirrors
 * web/app/components/demo/use-demo-stream.ts.
 */
export interface SandboxSessionDTO {
  id: string;
  workspaceId?: string;
  phone: string | null;
  status: string;
  /**
   * STI discriminator. Defaults to 'whatsapp' when absent to preserve
   * backward compatibility with any frontend caller that pre-dates the
   * IG rollout. Backend always populates it post-Task 1.
   */
  type?: 'whatsapp' | 'instagram' | 'facebook';
  /** Provider-generic sender id carried on stream events (PSID for facebook). */
  senderId?: string | null;
  /** facebook-only (AIT-621) — PSID of the Messenger sender bound through the sandbox Page. */
  facebookSenderId?: string | null;
  /** facebook-only — display name resolved at bind time; null until resolved or on failure. */
  facebookSenderName?: string | null;
  /** IG-only — opaque IGSID for the customer that bound this session.
   *  Renamed from `instagramSenderId` to self-document the IG actor
   *  (sender = the end user). */
  senderInstagramId?: string | null;
  /** IG-only — sandbox IG Business account this session is anchored on.
   *  Renamed from `instagramAccountId` to self-document the IG actor
   *  (account = the shared sandbox account). */
  accountInstagramId?: string | null;
  /** IG-only — customer handle resolved at bind time via the Meta
   *  Profile API. NULL while the async resolver hasn't completed
   *  (BOUND payload races the fire-and-forget lookup); a follow-up
   *  UPDATED event carries the populated handle. UI displays as
   *  `@${senderInstagramUsername}` when present and falls back to
   *  `senderInstagramId` (raw IGSID) otherwise. Renamed from
   *  `instagramSenderUsername` to match the senderInstagram* pattern. */
  senderInstagramUsername?: string | null;
  origin?: 'manual' | 'demo_handoff' | 'demo_anonymous';
  webhookUrl?: string | null;
  hostname?: string | null;
  activatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Present on snapshot-loaded sessions; only some SSE events carry them
   *  (e.g. sandbox.updated does, others omit). Preserved via object spread
   *  merge on upsert so downstream UI (e.g. "Copy starter kit .env")
   *  continues to read a valid value after an event. */
  accessToken?: string;
  hmacSecret?: string;
  verifyToken?: string;
  createdByUserId?: string | null;
  cloudflareTunnelId?: string | null;
  sandboxPhoneNumberId?: string | null;
  workspaceName?: string | null;
}

// Augment Window with the sandbox stream test seam so Playwright tests
// can emit mock SSE frames without a double-cast.
declare global {
  interface Window {
    __mockSandboxStreamEmit?: (event: string, payload: unknown) => void;
  }
}

export interface UseSandboxSessionsStreamResult {
  sessions: SandboxSessionDTO[];
  loading: boolean;
  error: string | null;
  /**
   * Manually replace the entire sessions array. Useful when a page
   * has just done an authenticated fetch on /sandbox/sessions
   * (e.g. as a fallback against best-effort SSE publish failure)
   * and wants to reseed state.
   */
  applySnapshot: (sessions: SandboxSessionDTO[]) => void;
  /**
   * Optimistically upsert a single session into local state (e.g.
   * after the page successfully PATCHed /sandbox/sessions/:id/...
   * and got the updated row back). Mirrors the `bound`/`updated`
   * SSE event handler — by-id upsert with merge semantics. Pages
   * SHOULD call this on dialog success so the UI doesn't depend
   * on best-effort SSE delivery.
   */
  applySession: (session: SandboxSessionDTO) => void;
  /**
   * Optimistically remove a session by id (mirrors the `deleted`
   * SSE event handler).
   */
  removeSession: (id: string) => void;
}

type ApplyPayload = {
  eventId: string;
  sessions?: SandboxSessionDTO[];
  session?: SandboxSessionDTO;
};

export function useSandboxSessionsStream(): UseSandboxSessionsStreamResult {
  const [sessions, setSessions] = useState<SandboxSessionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  // Tracks whether the first snapshot has been received. Transport errors
  // before the snapshot should not clear loading — the page would incorrectly
  // show the "zero sessions" flow for a workspace that already has active
  // sessions. Post-snapshot errors can safely clear loading because the
  // sessions array already reflects real state.
  const snapshotReceivedRef = useRef(false);
  // Per-handshake buffer for events that arrive BEFORE the snapshot frame.
  // Backend's stream controller registers the response then runs listSessions,
  // so a Pub/Sub event published while listSessions is still reading can be
  // delivered to the client before the snapshot — and the snapshot's older DB
  // view would clobber the just-applied event. We buffer pre-snapshot events
  // and replay them after the snapshot is applied so the snapshot becomes the
  // base and events compose on top in their published order.
  const pendingSnapshotRef = useRef(true);
  const eventBufferRef = useRef<Array<{ event: string; payload: unknown }>>([]);
  // True once we've kicked off the REST fallback for this effect run, to
  // avoid hammering /sandbox/sessions on every fetchEventSource retry tick.
  const fallbackTriedRef = useRef(false);
  // Watchdog timer armed when the SSE handshake opens 200 OK. If the first
  // snapshot doesn't arrive within SNAPSHOT_TIMEOUT_MS, the page is stuck
  // on a perpetual loader while a proxy buffers the stream silently. The
  // watchdog kicks the REST fallback so the page renders real state. Cleared
  // when the snapshot arrives or the stream errors/closes.
  const snapshotWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { activeWorkspace, sessionScoped } = useWorkspace();
  const { showErrorPage } = useError();
  const activeWorkspaceId = activeWorkspace?.id;
  // Stable ref so the effect can call showErrorPage without depending on
  // it (showErrorPage identity may change across renders and we don't
  // want to thrash the SSE connection on unrelated context updates).
  const showErrorPageRef = useRef(showErrorPage);
  useEffect(() => {
    showErrorPageRef.current = showErrorPage;
  }, [showErrorPage]);

  // Reset state at render time when the active workspace changes — without
  // this, the previous workspace's sessions linger until the new stream's
  // snapshot arrives, and stay on screen forever if the new handshake errors
  // before its first snapshot. Using the documented "previous-prop" pattern
  // (https://react.dev/learn/you-might-not-need-an-effect) keeps the state
  // reset out of useEffect to avoid cascading-render lint, and runs in the
  // SAME render that detects the change. Ref resets happen in the effect
  // (refs cannot be mutated during render in React 19).
  const [resetKey, setResetKey] = useState<string | undefined>(activeWorkspaceId);
  if (resetKey !== activeWorkspaceId) {
    setResetKey(activeWorkspaceId);
    setSessions([]);
    setLoading(true);
    setError(null);
  }

  const processEvent = useCallback((event: string, payload: unknown) => {
    const data = payload as ApplyPayload | null;
    if (event === 'snapshot' && data?.sessions) {
      setSessions(data.sessions);
      snapshotReceivedRef.current = true;
      setLoading(false);
      // Snapshot arrived — clear the watchdog so we don't hold a no-op
      // timer in the event loop until it fires.
      if (snapshotWatchdogRef.current !== null) {
        clearTimeout(snapshotWatchdogRef.current);
        snapshotWatchdogRef.current = null;
      }
      return;
    }
    if (!data?.session) return;
    if (
      event === 'sandbox.session.bound' ||
      event === 'sandbox.session.activated' ||
      event === 'sandbox.session.updated'
    ) {
      setSessions((prev) => {
        const idx = prev.findIndex((s) => s.id === data.session!.id);
        // New session — prepend so the page's newest-first ordering
        // (snapshot is `createdAt desc`) is preserved. Without this, a
        // freshly bound session lands at the end and SandboxPage's
        // `activeSessions[0]` CTA keeps targeting the older row.
        if (idx === -1) return [data.session!, ...prev];
        const next = prev.slice();
        // Merge into the existing entry so snapshot-only fields (accessToken,
        // hmacSecret, createdByUserId, cloudflareTunnelId, etc.) that the
        // narrower SandboxSessionEventPayload does not carry are preserved.
        next[idx] = { ...prev[idx], ...data.session! };
        return next;
      });
      return;
    }
    if (event === 'sandbox.session.deactivated') {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === data.session!.id ? { ...s, status: data.session!.status } : s,
        ),
      );
      return;
    }
    if (event === 'sandbox.session.deleted') {
      setSessions((prev) => prev.filter((s) => s.id !== data.session!.id));
      return;
    }
  }, []);

  const apply = useCallback(
    (event: string, payload: unknown) => {
      const data = payload as ApplyPayload | null;
      if (!data?.eventId) return;
      if (seenRef.current.has(data.eventId)) return;

      // Buffer non-snapshot events that arrive before this handshake's snapshot.
      // Don't dedup-mark them yet — the replay path below will mark + apply.
      if (event !== 'snapshot' && pendingSnapshotRef.current) {
        eventBufferRef.current.push({ event, payload });
        return;
      }

      seenRef.current.add(data.eventId);
      processEvent(event, payload);

      // Snapshot just applied — drain the per-handshake buffer in order so
      // events delivered during listSessions land on top of the snapshot base.
      if (event === 'snapshot') {
        pendingSnapshotRef.current = false;
        const buffered = eventBufferRef.current;
        eventBufferRef.current = [];
        for (const b of buffered) {
          const bData = b.payload as ApplyPayload | null;
          if (!bData?.eventId) continue;
          if (seenRef.current.has(bData.eventId)) continue;
          seenRef.current.add(bData.eventId);
          processEvent(b.event, b.payload);
        }
      }
    },
    [processEvent],
  );

  useEffect(() => {
    if (!activeWorkspaceId || !sessionScoped) return;

    // Reset per-handshake refs at the start of every effect run (mount and
    // workspace switch). Refs can't be reset during render in React 19, so
    // this runs after the render-time state reset above. The previous
    // effect's cleanup has already aborted the old stream before this runs.
    seenRef.current = new Set();
    snapshotReceivedRef.current = false;
    pendingSnapshotRef.current = true;
    eventBufferRef.current = [];
    fallbackTriedRef.current = false;

    const abortController = new AbortController();
    window.__mockSandboxStreamEmit = (event, payload) => apply(event, payload);

    void (async () => {
      try {
        const headers: Record<string, string> = {};
        if (activeWorkspaceId) {
          headers['X-Workspace-Id'] = activeWorkspaceId;
        }
        // NOTE: do NOT attach X-Handoff-JWT here. peekHandoffJwt() is
        // one-shot (reads + removes from sessionStorage); attaching it would
        // race the explicit POST /sandbox/claim-handoff in
        // useDemoHandoffClaim, which is the ONLY path that actually adopts
        // the demo session into the workspace. AuthService.validateToken-
        // AndGetUser does not adopt on org-scoped requests, so consuming
        // the JWT here would silently drop the handoff. The claim flow
        // succeeds independently and either publishes a BOUND event (if
        // wired) or is picked up by the focus/visibility safety-net refetch.

        // Shared REST fallback used by both the non-2xx onopen path AND the
        // pre-snapshot transport-error onerror path. Returns the parsed list
        // (or null on any failure) and the response status if the request
        // reached the server. The CALLER is responsible for applying the
        // result to state (so the racy onerror path can guard against a
        // stream snapshot landing before the GET resolves) and for surfacing
        // server errors via the GET error page (5xx => showErrorPage),
        // matching the legacy useFetch behavior.
        type FallbackResult = {
          list: SandboxSessionDTO[] | null;
          status: number | null;
        };
        const restFallback = async (): Promise<FallbackResult> => {
          try {
            const fbResp = await fetch(`${API_URL}/sandbox/sessions`, {
              headers,
              credentials: 'include',
              signal: abortController.signal,
            });
            if (!fbResp.ok) {
              // Mirror useFetch: full-page error UI for backend 5xx GETs so
              // the user sees "server error" instead of a misleading empty
              // bind flow.
              if (fbResp.status >= 500) {
                showErrorPageRef.current({
                  message: 'Server error',
                  url: '/sandbox/sessions',
                  status: fbResp.status,
                });
              }
              return { list: null, status: fbResp.status };
            }
            // /sandbox/sessions returns a bare SandboxSession[] (not
            // {sessions: [...]}). Treat any non-array as a no-op so a
            // future shape change doesn't silently empty the page.
            const data = (await fbResp.json()) as
              | SandboxSessionDTO[]
              | { sessions?: SandboxSessionDTO[] };
            const list = Array.isArray(data) ? data : data?.sessions;
            return {
              list: Array.isArray(list) ? list : null,
              status: fbResp.status,
            };
          } catch {
            // Network failure / abort — best-effort; caller surfaces its
            // own error state.
            return { list: null, status: null };
          }
        };

        // Apply a fallback result. Used by both onopen and onerror paths.
        // The REST list IS the initial snapshot from the page's perspective:
        // mark snapshotReceived AND drain the per-handshake event buffer so
        // any events that landed before the fallback get applied on top, and
        // future events stop buffering behind pendingSnapshotRef. Without
        // this, the slow-snapshot fallback path itself silently stops live
        // updates — the exact case it was supposed to recover.
        const applyFallbackResult = (list: SandboxSessionDTO[] | null) => {
          if (!list) return;
          setSessions(list);
          snapshotReceivedRef.current = true;
          // Drain buffered events on top of the REST list.
          const buffered = eventBufferRef.current;
          eventBufferRef.current = [];
          pendingSnapshotRef.current = false;
          for (const b of buffered) {
            const bData = b.payload as ApplyPayload | null;
            if (!bData?.eventId) continue;
            if (seenRef.current.has(bData.eventId)) continue;
            seenRef.current.add(bData.eventId);
            processEvent(b.event, b.payload);
          }
        };

        await fetchEventSource(`${API_URL}/sandbox/sessions/stream`, {
          headers,
          credentials: 'include',
          signal: abortController.signal,
          onopen: async (response) => {
            if (!response.ok) {
              // Stream is broken — fall back to a one-shot REST fetch so the
              // page renders the user's actual sessions instead of a
              // misleading "no sessions" / bind flow. Synchronous awaits
              // here, no race with retries (we haven't returned yet).
              fallbackTriedRef.current = true;
              const { list } = await restFallback();
              applyFallbackResult(list);
              setLoading(false);
              setError(`Stream open failed: ${response.status}`);
              // For permanent / config / auth failures (4xx that won't
              // self-heal), throw so fetch-event-source stops retrying.
              // For 5xx / 408 / 429 — likely a transient backend restart,
              // proxy blip, or rate-limit — return undefined so the library
              // keeps reconnecting and live updates resume when the backend
              // comes back. Without this, a brief deploy or 502 silently
              // disables live updates for the rest of the tab session.
              const status = response.status;
              const isTransient = status >= 500 || status === 408 || status === 429;
              if (isTransient) return;
              throw new UserBlockingError({
                message: `Sandbox sessions stream open failed`,
                code: 'SANDBOX_STREAM_OPEN_FAILED',
                details: { status },
                userMessage:
                  'Could not connect to live updates. Please refresh.',
              });
            }
            // Re-arm per-handshake state for the new connection. Without
            // resetting fallbackTriedRef here, the watchdog/onerror fallback
            // is permanently disabled after the first connection — meaning
            // a 55-min reconnect that delays its snapshot would leave the
            // tab stuck on stale data. snapshotReceivedRef is intentionally
            // NOT reset (it's the ever-received indicator used by onerror
            // to decide whether transport errors should clear loading).
            // The current handshake's snapshot status lives on
            // pendingSnapshotRef, which IS reset.
            pendingSnapshotRef.current = true;
            eventBufferRef.current = [];
            fallbackTriedRef.current = false;

            // Arm the snapshot watchdog. A buffering proxy can keep the
            // stream open indefinitely without flushing any frame; without
            // this guard the page would spin forever on the initial loader.
            // 10s is well above normal snapshot latency (~100ms) and below
            // any plausible "user gives up" threshold.
            const SNAPSHOT_TIMEOUT_MS = 10_000;
            if (snapshotWatchdogRef.current !== null) {
              clearTimeout(snapshotWatchdogRef.current);
            }
            snapshotWatchdogRef.current = setTimeout(() => {
              snapshotWatchdogRef.current = null;
              // Use pendingSnapshotRef (per-handshake) — snapshotReceivedRef
              // would be true on every reconnect after the first, disabling
              // the watchdog forever.
              if (!pendingSnapshotRef.current) return;
              if (fallbackTriedRef.current) return;
              fallbackTriedRef.current = true;
              void (async () => {
                const { list } = await restFallback();
                if (pendingSnapshotRef.current) {
                  applyFallbackResult(list);
                }
                setLoading(false);
              })();
            }, SNAPSHOT_TIMEOUT_MS);
          },
          onmessage: (ev) => {
            if (!ev.event || ev.event === ':') return;
            if (ev.event === 'closed') return;
            try {
              const payload = JSON.parse(ev.data);
              apply(ev.event, payload);
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err);
              setError(message);
            }
          },
          onclose: () => {
            // fetchEventSource does NOT auto-retry on a clean close. Both
            // the 55-min soft-close in SandboxStreamController and the
            // snapshot-failed branch end the response normally — without
            // a throw here the library treats the stream as "completed"
            // and stops, leaving the page stale until a full reload.
            // Throwing tells the library to retry; the AbortController
            // signal in `signal` aborts the retry on unmount, so this
            // does not loop indefinitely after the user navigates away.
            throw new UserBlockingError({
              message: 'Sandbox sessions stream closed by server',
              code: 'SANDBOX_STREAM_SERVER_CLOSED',
              userMessage: '',
            });
          },
          onerror: (err) => {
            if (
              err instanceof UserBlockingError &&
              err.code === 'SANDBOX_STREAM_OPEN_FAILED' &&
              typeof err.details.status === 'number' &&
              err.details.status >= 400 &&
              err.details.status < 500
            ) {
              throw err;
            }
            // Transport-level failure (backend down, proxy reset, browser
            // offline). Surface the error string regardless.
            // Returning undefined lets fetch-event-source's native retry
            // loop run; we keep that on so the page recovers when the
            // backend comes back.
            const message = err instanceof Error ? err.message : String(err);
            // Loading clear is gated on EVER having received a snapshot —
            // once the page has rendered real data, transport errors
            // shouldn't put it back on the loader.
            if (snapshotReceivedRef.current) {
              setLoading(false);
            }
            // Fallback decision is gated on THIS handshake's pending status
            // (pendingSnapshotRef), so a delayed snapshot on reconnect can
            // re-trigger a one-shot fetch even if a previous handshake's
            // snapshot already populated state.
            if (pendingSnapshotRef.current && !fallbackTriedRef.current) {
              fallbackTriedRef.current = true;
              void (async () => {
                const { list } = await restFallback();
                // Race guard: if the SSE retry succeeded with a fresh
                // snapshot during our GET, pendingSnapshotRef is now false
                // and the SSE data is newer. Skip the apply.
                if (pendingSnapshotRef.current) {
                  applyFallbackResult(list);
                }
                setLoading(false);
              })();
            }
            setError(`Stream connection failed: ${message}`);
            return undefined;
          },
        });
      } catch (err) {
        if (abortController.signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
      }
    })();

    return () => {
      abortController.abort();
      delete window.__mockSandboxStreamEmit;
      if (snapshotWatchdogRef.current !== null) {
        clearTimeout(snapshotWatchdogRef.current);
        snapshotWatchdogRef.current = null;
      }
    };
  }, [apply, activeWorkspaceId, sessionScoped]);

  const applySnapshot = useCallback((incoming: SandboxSessionDTO[]) => {
    snapshotReceivedRef.current = true;
    setSessions(incoming);
    setLoading(false);
  }, []);

  const applySession = useCallback((session: SandboxSessionDTO) => {
    setSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === session.id);
      // Mirror the SSE upsert path: prepend on insert so newest-first
      // ordering (createdAt desc) survives optimistic UI as well.
      if (idx === -1) return [session, ...prev];
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...session };
      return next;
    });
  }, []);

  const removeSession = useCallback((id: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return { sessions, loading, error, applySnapshot, applySession, removeSession };
}
